/**
 * S3.5 - Section "Mandataires" de la page parametres CEE.
 * CRUD : liste + edit modal + add + delete (refuse si dossiers attaches).
 */

import { useState, useEffect } from 'react';
import * as ceeApi from '../../api/cee.js';

const EMPTY = { code: '', nom: '', delegataire: 'LORIS ENR', contact_nom: '', contact_email: '', active: true };

/**
 * @returns {JSX.Element}
 */
export default function MandatairesAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // null = closed, EMPTY = create, {id, ...} = edit

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await ceeApi.listMandataires();
      setItems(r);
    } catch (err) {
      setError(err.message || 'Erreur chargement mandataires');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSave = async (form) => {
    try {
      if (form.id) {
        await ceeApi.updateMandataire(form.id, form);
      } else {
        await ceeApi.createMandataire(form);
      }
      setEditing(null);
      await load();
    } catch (err) {
      setError(err.message || 'Erreur sauvegarde');
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Supprimer ce mandataire ?')) return;
    try {
      await ceeApi.deleteMandataire(id);
      await load();
    } catch (err) {
      setError(err.message || 'Erreur suppression');
    }
  };

  const onToggleActive = async (m) => {
    try {
      await ceeApi.updateMandataire(m.id, { active: !m.active });
      await load();
    } catch (err) {
      setError(err.message || 'Erreur toggle');
    }
  };

  return (
    <section className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold text-[#0f172a]">Mandataires</h2>
        <button type="button" onClick={() => setEditing({ ...EMPTY })}
          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          + Ajouter
        </button>
      </div>
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 mb-3 text-sm">{error}</div>}
      {loading ? <p className="text-sm text-gray-500">Chargement...</p> : (
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-gray-500 border-b">
            <tr>
              <th className="py-2">Code</th>
              <th>Nom</th>
              <th>Contact</th>
              <th>Delegataire</th>
              <th>Actif</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((m) => (
              <tr key={m.id} className="border-b last:border-0">
                <td className="py-2 font-mono">{m.code}</td>
                <td>{m.nom}</td>
                <td className="text-gray-600">{m.contact_nom || '-'}<br />{m.contact_email && <span className="text-xs">{m.contact_email}</span>}</td>
                <td className="text-gray-600">{m.delegataire}</td>
                <td>{m.active ? 'Oui' : 'Non'}</td>
                <td className="space-x-2">
                  <button type="button" onClick={() => setEditing({ ...m })} className="text-xs text-blue-600 hover:underline">Editer</button>
                  <button type="button" onClick={() => onToggleActive(m)} className="text-xs text-orange-600 hover:underline">{m.active ? 'Desactiver' : 'Activer'}</button>
                  <button type="button" onClick={() => onDelete(m.id)} className="text-xs text-red-600 hover:underline">Supprimer</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="6" className="text-center text-gray-400 py-3">Aucun mandataire</td></tr>}
          </tbody>
        </table>
      )}
      {editing && <EditModal initial={editing} onSave={onSave} onClose={() => setEditing(null)} />}
    </section>
  );
}

function EditModal({ initial, onSave, onClose }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5">
        <h3 className="font-bold mb-3">{form.id ? 'Editer' : 'Ajouter'} mandataire</h3>
        <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="space-y-2 text-sm">
          <input className="w-full border rounded-md px-2 py-1.5" placeholder="Code (ex: FEH)" value={form.code} onChange={(e) => set('code', e.target.value)} required disabled={!!form.id} />
          <input className="w-full border rounded-md px-2 py-1.5" placeholder="Nom" value={form.nom} onChange={(e) => set('nom', e.target.value)} required />
          <input className="w-full border rounded-md px-2 py-1.5" placeholder="Delegataire" value={form.delegataire || ''} onChange={(e) => set('delegataire', e.target.value)} />
          <input className="w-full border rounded-md px-2 py-1.5" placeholder="Contact nom" value={form.contact_nom || ''} onChange={(e) => set('contact_nom', e.target.value)} />
          <input className="w-full border rounded-md px-2 py-1.5" placeholder="Contact email" type="email" value={form.contact_email || ''} onChange={(e) => set('contact_email', e.target.value)} />
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!form.active} onChange={(e) => set('active', e.target.checked)} /> Actif</label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm border rounded-md">Annuler</button>
            <button type="submit" className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md">Sauvegarder</button>
          </div>
        </form>
      </div>
    </div>
  );
}
